import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [growOpen, setGrowOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Scroll direction logic
      if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
        setShowNavbar(false);
        setIsMobileMenuOpen(false);
      } else {
        setShowNavbar(true);
      }

      // Scrolled state for styling
      setScrolled(currentScrollY > 20);
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setSectorsOpen(false);
    setAboutOpen(false);
    setGrowOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <motion.nav
      className={`navbar-new ${scrolled ? "scrolled" : ""} ${!showNavbar ? "hidden" : ""}`}
      initial={{ y: 0 }}
      animate={{ y: showNavbar ? 0 : -140 }}
      transition={{ duration: 0.3 }}
    >
      {/* Top Bar - Contact Info */}
      <div className="navbar-top-bar">
        <div className="navbar-container">
          <div className="top-bar-content">
            <div className="contact-info">
              <a href="tel:+94719645645" className="contact-item">
                <span className="icon">📞</span> +94 719645645
              </a>
              <a href="mailto:info@theciap.com" className="contact-item">
                <span className="icon">📧</span> ciapinfo@theciap.com
              </a>
            </div>
            <div className="top-bar-links">
              <Link to="/news">News</Link>
              <Link to="/join">Careers</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="navbar-main">
        <div className="navbar-container">
          <div className="navbar-content-wrapper">
            {/* Left 25% - Logo */}
            <Link to="/" className="navbar-logo-new" onClick={closeMobileMenu}>
              {!logoError ? (
                <img
                  src="/images/ciap-logo.png"
                  alt="CIAP Logo"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span style={{ fontWeight: "bold", fontSize: "1.5rem", color: "#004a99" }}>CIAP</span>
              )}
            </Link>

            {/* Middle 50% - Navigation Menu */}
            <ul className="nav-menu">
              <li className={isActive("/") ? "active" : ""}>
                <Link to="/" onClick={closeMobileMenu}>Home</Link>
              </li>
              <li
                className={`dropdown-item ${aboutOpen ? "open" : ""}`}
                onMouseEnter={() => setAboutOpen(true)}
                onMouseLeave={() => setAboutOpen(false)}
              >
                <Link to="/about" onClick={closeMobileMenu}>About Us</Link>
                <AnimatePresence>
                  {aboutOpen && (
                    <motion.ul
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      className="dropdown-menu"
                    >
                      <li><Link to="/leadership" onClick={closeMobileMenu}>Leadership</Link></li>
                      <li><Link to="/faq" onClick={closeMobileMenu}>FAQ</Link></li>

                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>

              <li
                className={`dropdown-item ${sectorsOpen ? "open" : ""}`}
                onMouseEnter={() => setSectorsOpen(true)}
                onMouseLeave={() => setSectorsOpen(false)}
              >
                <Link to="/sectors" className="nav-link">Services</Link>
                <AnimatePresence>
                  {sectorsOpen && (
                    <motion.ul
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      className="dropdown-menu"
                    >
                      <li><Link to="/sectors/marine" onClick={closeMobileMenu}>Marine Engineering</Link></li>
                      <li><Link to="/sectors/docking" onClick={closeMobileMenu}>Pre-Docking Solutions</Link></li>
                      <li><Link to="/sectors/shipbuild" onClick={closeMobileMenu}>Ship & Boat Building</Link></li>
                      <li><Link to="/sectors/husbandry" onClick={closeMobileMenu}>Husbandry Services</Link></li>
                      <li><Link to="/sectors/spares" onClick={closeMobileMenu}>Spares & Store Supply</Link></li>
                      <li><Link to="/sectors/industrial" onClick={closeMobileMenu}>Industrial Engineering</Link></li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>

              {/* <li className={isActive("/contact") ? "active" : ""}>
                <Link to="/contact" onClick={closeMobileMenu}>Contact</Link>
              </li> */}

              <li
                className={`dropdown-item ${growOpen ? "open" : ""}`}
                onMouseEnter={() => setGrowOpen(true)}
                onMouseLeave={() => setGrowOpen(false)}
              >
                <span className="nav-link">Register </span>
                <AnimatePresence>
                  {growOpen && (
                    <motion.ul
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      className="dropdown-menu"
                    >
                      <li><Link to="/srform" onClick={closeMobileMenu}>Suppliers</Link></li>
                      <li><Link to="/srform" onClick={closeMobileMenu}>Services</Link></li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            </ul>

            {/* Right 25% - Actions */}
            <div className="navbar-actions">
              <Link to="/contact" className="cta-button">
                Contact Us
              </Link>

              {/* Mobile Toggle */}
              <button
                className={`mobile-toggle ${isMobileMenuOpen ? "active" : ""}`}
                onClick={toggleMobileMenu}
                aria-label="Toggle Menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu-overlay"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            <div className="mobile-menu-content">
              <ul className="mobile-nav-links">
                <li><Link to="/" onClick={closeMobileMenu}>Home</Link></li>

                <li className="mobile-dropdown-item">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => setAboutOpen(!aboutOpen)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <span>About Us</span>
                    <motion.span
                      animate={{ rotate: aboutOpen ? 180 : 0 }}
                      style={{ fontSize: '0.8rem', marginLeft: '10px' }}
                    >
                      ▼
                    </motion.span>
                  </div>

                  <AnimatePresence>
                    {aboutOpen && (
                      <motion.ul
                        className="mobile-submenu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        style={{ listStyle: 'none', paddingLeft: '20px', overflow: 'hidden' }}
                      >
                        <li><Link to="/about" onClick={closeMobileMenu}>About Us</Link></li>
                        <li><Link to="/leadership" onClick={closeMobileMenu}>Leadership</Link></li>
                      <li><Link to="/faq" onClick={closeMobileMenu}>FAQ</Link></li>


                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>

                {/* Dropdown Section for Services */}
                <li className="mobile-dropdown-item">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => setSectorsOpen(!sectorsOpen)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <span>Services</span>
                    <motion.span
                      animate={{ rotate: sectorsOpen ? 180 : 0 }}
                      style={{ fontSize: '0.8rem', marginLeft: '10px' }}
                    >
                      ▼
                    </motion.span>
                  </div>

                  <AnimatePresence>
                    {sectorsOpen && (
                      <motion.ul
                        className="mobile-submenu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        style={{ listStyle: 'none', paddingLeft: '20px', overflow: 'hidden' }}
                      >
                        <li><Link to="/sectors" onClick={closeMobileMenu}>All Services</Link></li>
                        <li><Link to="/sectors/marine" onClick={closeMobileMenu}>Marine Engineering</Link></li>
                        <li><Link to="/sectors/docking" onClick={closeMobileMenu}>Pre-Docking Solutions</Link></li>
                        <li><Link to="/sectors/shipbuild" onClick={closeMobileMenu}>Ship & Boat Building</Link></li>
                        <li><Link to="/sectors/husbandry" onClick={closeMobileMenu}>Husbandry Services</Link></li>
                        <li><Link to="/sectors/spares" onClick={closeMobileMenu}>Spares & Store Supply</Link></li>
                        <li><Link to="/sectors/industrial" onClick={closeMobileMenu}>Industrial Engineering</Link></li>


                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>

                <li className="mobile-dropdown-item">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => setGrowOpen(!growOpen)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <span>Register</span>
                    <motion.span
                      animate={{ rotate: growOpen ? 180 : 0 }}
                      style={{ fontSize: '0.8rem', marginLeft: '10px' }}
                    >
                      ▼
                    </motion.span>
                  </div>

                  <AnimatePresence>
                    {growOpen && (
                      <motion.ul
                        className="mobile-submenu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        style={{ listStyle: 'none', paddingLeft: '20px', overflow: 'hidden' }}
                      >
                      
                      <li><Link to="/srform" onClick={closeMobileMenu}>Suppliers</Link></li>
                      <li><Link to="/srform" onClick={closeMobileMenu}>Services</Link></li>


                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>

                <li><Link to="/news" onClick={closeMobileMenu}>News</Link></li>
                <li><Link to="/Join" onClick={closeMobileMenu}>Careers</Link></li>
              </ul>
              <Link to="/contact" className="cta-button mobile-cta" onClick={closeMobileMenu}>
                Contact Us
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
